import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import mixpanel from "mixpanel-browser";
import * as Sentry from "@sentry/react";

import { auth } from "_firebase";

import { checkUserSessionAPI, logoutUserAPI } from "utils/auth/api";

import { actions as userActions } from "store/users";

import Loading from "components/Loading";

const { fetchUserData, userStart, userFail } = userActions;

export const AuthContext = React.createContext({ user: null });

export const AuthProvider = ({ children }) => {
    const dispatch = useDispatch();

    const [user, setUser] = useState(null);
    const [pending, setPending] = useState(true);

    const checkUserSession = async () => {
        try {
            setPending(true);

            await dispatch(userStart());

            await checkUserSessionAPI()
                .then(async ({ data }) => {
                    if (!data) {
                        throw new Error("No session found");
                    }

                    const { user, token } = data;

                    const { uid = "" } = user;

                    await auth.signInWithCustomToken(token);

                    const userData = await dispatch(fetchUserData(uid));

                    if (typeof userData === "string") {
                        throw new Error(userData);
                    }

                    const { email, name, company, userMeta: { role, experienceLevel } = {} } = userData;

                    Sentry.setUser({
                        name,
                        email,
                        company,
                    });

                    await mixpanel.people.set({
                        $email: email,
                        $name: name,
                        Company: company,
                        Role: role,
                        "Expierience Level": experienceLevel,
                    });

                    mixpanel.identify(uid);
                })
                .catch((error) => {
                    throw error;
                });

            return await auth.currentUser;
        } catch (error) {
            const isNoSession = Boolean(localStorage.getItem("wasLoggedIn"));

            dispatch(userFail(isNoSession ? "Your session has expired. Please log in again." : ""));

            await auth.signOut();
            await logoutUserAPI();

            localStorage.removeItem("wasLoggedIn");
        }
    };

    useEffect(() => {
        (async () => {
            const user = await checkUserSession();

            setUser(user);
            setPending(false);
        })();
    }, []);

    if (pending) {
        return <Loading />;
    }

    return <AuthContext.Provider value={{ user, setUser }}>{children}</AuthContext.Provider>;
};
